/**
 * Day-1 Retention Quick Win #1 — FirstSkillModal (無 cost 第一個技能解鎖).
 *
 * Spec: docs/research/day1-retention-k3-onboarding.md quick win #1.
 *   「Day-1 無 cost 第一個技能解鎖彈窗,~50 行 modal CSS,0 schema bump」
 *   「home-scene.ts 加 if (progress.unlockedSkills.length === 0) showFirstSkillModal()」
 *
 * 為何 5 門派任選:
 *   • Khan Kids 5-mascot 鏡像理論 — 不同情緒投射對象
 *   • Squla 心理擁有感 — 主動選擇 = 承諾感
 *   • 與既有 FactionId schema (src/shared/types.ts:1) 完美對齊,0 schema bump
 *
 * 為何 24hr gate:
 *   • 不選 (close) 不代表「永遠拒絕」— 隔天 fresh 心情可能會選
 *   • 與 streak-pill (W#2) + next-day-preview (W#3) 同樣 localStorage 模式
 *   • 配合 K-3 注意力分散特性 (Piaget concrete operational stage)
 *
 * 為何 dispatch event:
 *   • W#2 streak-pill 已 ship master,W#3 next-day-preview BG 中
 *   • CustomEvent 'skill:first-unlock' 解耦 modal 與 home 邏輯,讓 home/streak/
 *     next-day-preview 都可獨立 listen 來 chain 動畫/toast/badge
 *   • 未來 game-side 也可 listen 同事件來 sync (不違反「學習與遊戲分離」—
 *     learn 端寫 progress,game 端只 read,通信僅靠 schema + event)
 *
 * 學習與遊戲分離 (CLAUDE.md 紅線):
 *   本 modal 屬「home 層 retention banner」(boss memory feedback_learn_pro_mode
 *   允許 home 彩光),絕對 NOT 注入 quiz/exam 作答畫面。
 */

import { FACTIONS, FACTION_COLORS } from '../../shared/factions';
import type { FactionId } from '../../shared/types';

/** 24 小時 (ms) — 不選 close 後 24hr 內不再顯。 */
export const FIRST_SKILL_MODAL_GATE_HOURS = 24;
export const FIRST_SKILL_MODAL_GATE_MS = FIRST_SKILL_MODAL_GATE_HOURS * 60 * 60 * 1000;

/** localStorage key — 與 next-day-preview 同樣的 namespaced 模式。 */
export const FIRST_SKILL_MODAL_DISMISSED_KEY = 'firstSkillModal:dismissedISO';

/**
 * 5 門派一句說明 — 去戲謔 / 去學科名 (boss memory feedback_learn_pro_mode
 * 鐵則:home 層可有彩光但文案仍要專業),只描述「遊戲內定位」。
 *
 * 來源:CLAUDE.md 五大門派表格 + DESIGN.md §2。
 */
export const FACTION_TAGLINES: Record<FactionId, string> = {
  celestial: '精準爆發 — 集中火力一擊制勝',
  runic:     '防禦控制 — 護盾與停滯壓制敵人',
  techno:    '速度偵察 — 機動穿透鎖定弱點',
  elemental: '元素持續 — 火/冰/雷持續傷害',
  dominion:  '資源支援 — 攻速與免疫加成全隊',
};

/**
 * Modal 開啟後 onSelect 觸發的 CustomEvent 名稱。
 * Detail payload: { factionKey: FactionId }
 */
export const FIRST_SKILL_UNLOCK_EVENT = 'skill:first-unlock';

/**
 * 判斷此次 home render 是否要顯示 FirstSkillModal。
 *
 * 4 條 gate (任 1 fail → 不顯):
 *   1. unlockedSkills 為空(已解鎖過 → 沒理由再彈)
 *   2. dismissedISO 未設 OR 已超過 24hr(避免每次 home 開都跳)
 *
 * @param unlockedSkillsLen 目前 progress.unlockedSkills.length (caller 傳入,
 *   避免本檔依賴 progress full shape — 純展示元件)。
 * @param dismissedISO 上次 close 的 ISO timestamp;undefined / 空 = 從未 dismiss。
 * @param nowMs 現在 unix ms;預設 Date.now()。測試用 inject。
 * @returns true = 應該顯示
 */
export function shouldShowFirstSkillModal(
  unlockedSkillsLen: number,
  dismissedISO: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (unlockedSkillsLen > 0) return false;
  if (!dismissedISO) return true;
  const parsed = Date.parse(dismissedISO);
  if (Number.isNaN(parsed)) return true; // bad ISO → safe default = 顯
  const elapsed = nowMs - parsed;
  return elapsed >= FIRST_SKILL_MODAL_GATE_MS;
}

/**
 * Render the FirstSkillModal overlay into `container` (typically document.body).
 *
 * Behaviour:
 *   • overlay fade-in 300ms (opacity 0 → 1)
 *   • modal scale-in 0.9 → 1.0 cubic-bezier elastic-ish 400ms
 *   • ESC key → onClose
 *   • click overlay (背景空白處) → onClose
 *   • click modal body → 不觸發 onClose
 *   • 「選擇」按鈕 → onSelect(factionKey) + close
 *   • 「關閉」按鈕 → onClose
 *
 * Idempotent: 若 container 內已存在 data-testid="first-skill-modal" 則先 remove。
 *
 * @param container 通常傳 document.body
 * @param onSelect callback(factionKey) — caller 負責寫 progress + dispatch event
 * @param onClose callback — caller 負責寫 localStorage dismiss 標記
 */
export function renderFirstSkillModal(
  container: HTMLElement,
  onSelect: (faction: FactionId) => void,
  onClose: () => void,
): void {
  if (!container) return;

  // Idempotent — 移除舊 modal
  const prior = container.querySelector('[data-testid="first-skill-modal"]');
  if (prior) prior.remove();

  // ── Overlay ──
  const overlay = document.createElement('div');
  overlay.setAttribute('data-testid', 'first-skill-modal');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', '歡迎!送你第一個技能');
  overlay.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'right:0',
    'bottom:0',
    'background:rgba(0,0,0,0.55)',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'z-index:9999',
    'opacity:0',
    'transition:opacity 300ms ease',
    'padding:16px',
  ].join(';');

  // ── Modal body ──
  const modal = document.createElement('div');
  modal.setAttribute('data-testid', 'first-skill-modal-body');
  modal.style.cssText = [
    'background:#FAFAFA',
    'border-radius:16px',
    'padding:24px',
    'max-width:520px',
    'width:100%',
    'box-shadow:0 8px 32px rgba(0,0,0,0.35)',
    'transform:scale(0.9)',
    'opacity:0',
    'transition:transform 400ms cubic-bezier(0.34,1.56,0.64,1),opacity 300ms ease',
    'max-height:90vh',
    'overflow-y:auto',
  ].join(';');

  // ── Title (去戲謔文案,boss memory feedback_learn_pro_mode) ──
  const title = document.createElement('h2');
  title.setAttribute('data-testid', 'first-skill-modal-title');
  title.textContent = '歡迎!送你第一個技能';
  title.style.cssText = [
    'margin:0 0 8px',
    'font-size:22px',
    'color:#212121',
    'font-weight:700',
    'text-align:center',
  ].join(';');

  const subtitle = document.createElement('p');
  subtitle.textContent = '從五大門派任選 1 個專屬技能,馬上就能在戰鬥裡使用。';
  subtitle.style.cssText = [
    'margin:0 0 20px',
    'font-size:14px',
    'color:#546E7A',
    'text-align:center',
    'line-height:1.5',
  ].join(';');

  modal.appendChild(title);
  modal.appendChild(subtitle);

  // ── Faction list ──
  const list = document.createElement('div');
  list.setAttribute('data-testid', 'first-skill-modal-list');
  list.style.cssText = [
    'display:flex',
    'flex-direction:column',
    'gap:10px',
    'margin-bottom:16px',
  ].join(';');

  const factionIds: FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];
  for (const fid of factionIds) {
    const meta = FACTIONS[fid];
    const colors = FACTION_COLORS[fid];

    const row = document.createElement('button');
    row.setAttribute('data-testid', `first-skill-faction-${fid}`);
    row.setAttribute('data-faction', fid);
    row.setAttribute('type', 'button');
    row.style.cssText = [
      'display:flex',
      'align-items:center',
      'gap:12px',
      `border:2px solid ${colors.primaryHex}`,
      'background:#FFFFFF',
      'border-radius:12px',
      'padding:12px 14px',
      'cursor:pointer',
      'text-align:left',
      'transition:transform 120ms ease,box-shadow 120ms ease',
      'font-family:inherit',
      'width:100%',
    ].join(';');

    const icon = document.createElement('span');
    icon.textContent = meta.emoji;
    icon.style.cssText = 'font-size:28px;line-height:1;flex:0 0 auto;';

    const textWrap = document.createElement('div');
    textWrap.style.cssText = 'display:flex;flex-direction:column;gap:2px;flex:1 1 auto;min-width:0;';

    const name = document.createElement('span');
    name.textContent = meta.displayName;
    name.style.cssText = `font-size:16px;font-weight:700;color:${colors.primaryHex};`;

    const tagline = document.createElement('span');
    tagline.textContent = FACTION_TAGLINES[fid];
    tagline.style.cssText = 'font-size:13px;color:#37474F;line-height:1.4;';

    textWrap.append(name, tagline);

    const cta = document.createElement('span');
    cta.setAttribute('data-testid', `first-skill-select-${fid}`);
    cta.textContent = '選擇';
    cta.style.cssText = [
      'flex:0 0 auto',
      `background:${colors.primaryHex}`,
      'color:#FFFFFF',
      'padding:6px 14px',
      'border-radius:999px',
      'font-size:13px',
      'font-weight:700',
    ].join(';');

    row.append(icon, textWrap, cta);

    row.addEventListener('click', (ev) => {
      ev.stopPropagation();
      // 防止 callback 內 throw 弄壞 close 流程
      try {
        onSelect(fid);
      } finally {
        cleanup();
      }
    });

    list.appendChild(row);
  }

  modal.appendChild(list);

  // ── Close button ──
  const closeBtn = document.createElement('button');
  closeBtn.setAttribute('data-testid', 'first-skill-modal-close');
  closeBtn.setAttribute('type', 'button');
  closeBtn.textContent = '稍後再選';
  closeBtn.style.cssText = [
    'display:block',
    'margin:8px auto 0',
    'background:transparent',
    'border:1px solid #B0BEC5',
    'color:#546E7A',
    'padding:8px 20px',
    'border-radius:999px',
    'cursor:pointer',
    'font-size:14px',
    'font-family:inherit',
  ].join(';');
  closeBtn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    try {
      onClose();
    } finally {
      cleanup();
    }
  });
  modal.appendChild(closeBtn);

  // Click modal body → 不關閉
  modal.addEventListener('click', (ev) => {
    ev.stopPropagation();
  });

  // Click overlay (背景空白處) → onClose
  overlay.addEventListener('click', () => {
    try {
      onClose();
    } finally {
      cleanup();
    }
  });

  overlay.appendChild(modal);
  container.appendChild(overlay);

  // ESC key → onClose
  const escHandler = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      try {
        onClose();
      } finally {
        cleanup();
      }
    }
  };
  document.addEventListener('keydown', escHandler);

  // Animate in (next frame so transition kicks in)
  // happy-dom 不會跑 RAF callback 但 setTimeout(0) 會跑
  setTimeout(() => {
    overlay.style.opacity = '1';
    modal.style.transform = 'scale(1)';
    modal.style.opacity = '1';
  }, 0);

  let disposed = false;
  function cleanup(): void {
    if (disposed) return;
    disposed = true;
    document.removeEventListener('keydown', escHandler);
    overlay.remove();
  }
}

/**
 * Helper to dispatch the global event after a faction is chosen.
 * Caller (home.ts) typically does:
 *   onSelect: (faction) => {
 *     progress.unlockedSkills.push(skillIdForFaction(faction));
 *     saveProgress(progress);
 *     dispatchFirstSkillUnlock(faction);
 *     showToast('解鎖成功!到 academy 試試新技能');
 *   }
 */
export function dispatchFirstSkillUnlock(faction: FactionId): void {
  window.dispatchEvent(new CustomEvent(FIRST_SKILL_UNLOCK_EVENT, {
    detail: { factionKey: faction },
  }));
}

/**
 * 把 FactionId 映射到該門派 Lv1 技能 id (見 src/shared/skills.ts catalog)。
 * 用於 caller 知道要 push 哪個 skill id 到 progress.unlockedSkills。
 */
export const FACTION_FIRST_SKILL_ID: Record<FactionId, string> = {
  celestial: 'meteor-cannon',
  runic:     'runic-shield',
  techno:    'shadow-drone',
  elemental: 'frost-cannon',
  dominion:  'eternal-sunshine',
};
